import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';
import SafeIcon from '../common/SafeIcon';
import { useCart } from '../contexts/CartContext';

const { FiArrowLeft, FiLock, FiShield, FiCheckCircle, FiTrash2, FiCreditCard } = FiIcons;
const { FaStripe } = FaIcons;

const Checkout = () => {
  const { cart, cartTotal, removeFromCart, clearCart } = useCart();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const navigate = useNavigate();

  const handlePayment = (e) => {
    e.preventDefault();
    setIsProcessing(true);
    // Simulate Stripe payment processing
    setTimeout(() => {
      setIsProcessing(false);
      setIsSuccess(true);
      clearCart();
    }, 2500);
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full text-center"
        >
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <SafeIcon icon={FiCheckCircle} className="text-green-600 text-4xl" />
          </div>
          <h1 className="text-3xl font-black text-gray-900 mb-4">Payment Successful!</h1>
          <p className="text-gray-500 mb-8">Thank you for your purchase. Your assessments have been added to your dashboard.</p>
          <Link to="/dashboard" className="inline-block w-full py-4 bg-blue-600 text-white rounded-2xl font-bold hover:bg-blue-700 transition-all">
            Go to Dashboard
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        <Link to="/assessments" className="inline-flex items-center text-gray-500 hover:text-blue-600 mb-8 font-bold text-sm">
          <SafeIcon icon={FiArrowLeft} className="mr-2" /> Back to Catalog
        </Link>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Order Summary */}
          <div>
            <h2 className="text-2xl font-black text-gray-900 mb-8">Order Summary</h2>
            <div className="space-y-4">
              {cart.length === 0 ? (
                <div className="bg-white p-12 rounded-3xl border border-gray-200 text-center">
                  <p className="text-gray-400 mb-4">Your cart is empty</p>
                  <Link to="/assessments" className="text-blue-600 font-bold">Browse Assessments</Link>
                </div>
              ) : (
                cart.map((item) => (
                  <div key={item.cartId} className="bg-white p-6 rounded-2xl border border-gray-200 flex justify-between items-center group">
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">{item.title}</h4>
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase ${
                          item.purchaseType === 'expert' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {item.purchaseType === 'expert' ? 'AI + Expert' : 'AI Analysis'}
                        </span>
                        <span className="text-sm font-bold text-gray-900">${item.price}</span>
                      </div>
                    </div>
                    <button onClick={() => removeFromCart(item.cartId)} className="p-2 text-gray-300 hover:text-red-600 transition-colors">
                      <SafeIcon icon={FiTrash2} />
                    </button>
                  </div>
                ))
              )}
            </div>

            {cart.length > 0 && (
              <div className="mt-8 pt-8 border-t border-gray-200">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-500">Subtotal</span>
                  <span className="font-bold text-gray-900">${cartTotal}</span>
                </div>
                <div className="flex justify-between items-center mb-6">
                  <span className="text-gray-500">Tax</span>
                  <span className="font-bold text-gray-900">$0.00</span>
                </div>
                <div className="flex justify-between items-center bg-blue-50 p-6 rounded-2xl">
                  <span className="text-blue-900 font-bold">Total Due</span>
                  <span className="text-3xl font-black text-blue-600">${cartTotal}</span>
                </div>
              </div>
            )}
          </div>

          {/* Payment Details */}
          <div className="bg-white rounded-[2.5rem] border border-gray-200 p-8 md:p-12 shadow-xl shadow-gray-200/50">
            <div className="flex items-center justify-between mb-10">
              <h2 className="text-2xl font-black text-gray-900 font-ubuntu">Payment Details</h2>
              <SafeIcon icon={FaStripe} className="text-4xl text-indigo-600" />
            </div>

            <form onSubmit={handlePayment} className="space-y-6">
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Card Information</label>
                <div className="relative">
                  <SafeIcon icon={FiCreditCard} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input required type="text" placeholder="1234 5678 1234 5678" className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Expiry</label>
                  <input required type="text" placeholder="MM/YY" className="w-full px-4 py-4 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">CVC</label>
                  <input required type="text" placeholder="123" className="w-full px-4 py-4 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all" />
                </div>
              </div>

              <div className="pt-6">
                <button 
                  disabled={isProcessing || cart.length === 0}
                  className="w-full py-5 bg-blue-600 text-white rounded-2xl font-bold text-lg hover:bg-blue-700 shadow-xl shadow-blue-100 transition-all flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isProcessing ? (
                    'Processing Payment...'
                  ) : (
                    <>
                      <SafeIcon icon={FiLock} /> Pay ${cartTotal} Securely
                    </>
                  )}
                </button>
              </div>

              <div className="flex items-center justify-center gap-4 pt-6 text-gray-400">
                <div className="flex items-center gap-1 text-[10px] uppercase font-bold tracking-widest">
                  <SafeIcon icon={FiShield} /> Secure
                </div>
                <div className="w-1 h-1 bg-gray-300 rounded-full" />
                <div className="flex items-center gap-1 text-[10px] uppercase font-bold tracking-widest">
                  <SafeIcon icon={FiLock} /> Encrypted
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;